import { computed, effect, inject, Injectable, PLATFORM_ID, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { isPlatformServer } from '@angular/common';

import { Store } from '@ngrx/store';
import { filter, Observable, switchMap } from 'rxjs';

import { MultivariateTestId, TestVariant, TestVariantWithId } from '@fcom/common/interfaces';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { ConfigService, LocalDate, TzDate } from '@fcom/core/index';
import { FragmentService } from '@fcom/common/services';
import { CmsArticle } from '@fcom/core-api';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { cartData } from '@fcom/booking/store/selectors';
import { FinnairCart } from '@fcom/dapi/api/models';

@Injectable({ providedIn: 'root' })
export class MoneyBackGuaranteeService {
  private store$: Store<BookingAppState> = inject(Store);
  private configService = inject(ConfigService);
  private multivariateTestService = inject(MultivariateTestService);
  private fragmentService = inject(FragmentService);
  private platformId = inject(PLATFORM_ID);

  readonly isVariantB: Signal<boolean>;
  readonly content$: Observable<CmsArticle | null>;
  readonly testVariantForPayment: Signal<TestVariant | undefined>;
  private readonly testVariant: Signal<TestVariantWithId>;
  private readonly currentOffer: Signal<FinnairCart>;
  private readonly isValidDate: Signal<boolean>;

  constructor() {
    this.testVariant = toSignal(
      this.multivariateTestService.getTestVariant(MultivariateTestId.MONEY_BACK_GUARANTEE, false),
      {
        initialValue: { id: MultivariateTestId.MONEY_BACK_GUARANTEE, variant: TestVariant.NOT_IN_TEST },
      }
    );

    this.currentOffer = toSignal(this.store$.pipe(cartData()), { initialValue: null });

    this.isValidDate = computed(() => {
      const cart = this.currentOffer();
      const isAward = !!cart?.prices?.unpaid?.total?.total?.totalPoints;
      if (!cart?.bounds?.length || isAward) {
        return false;
      }

      const departureDateTime = LocalDate.fromTzDate(TzDate.of(cart.bounds[0].departure.dateTime));
      const now = LocalDate.now();

      return departureDateTime.minusDays(7).gt(now);
    });

    this.isVariantB = computed(() => {
      if (!this.configService.cfg.enable24hMoneyBackGuarantee) return false;

      const variant = this.testVariant();
      return this.isValidDate() && variant?.variant === TestVariant.B;
    });

    effect(() => {
      if (isPlatformServer(this.platformId)) return;

      const variant = this.testVariant();
      const isDateValid = this.isValidDate();

      // do not report if date is not valid
      if (isDateValid && variant?.variant !== TestVariant.NOT_IN_TEST) {
        this.multivariateTestService.reportTest(MultivariateTestId.MONEY_BACK_GUARANTEE, variant?.variant);
      }
    });

    this.content$ = toObservable(this.isVariantB).pipe(
      filter(Boolean),
      switchMap(() => this.fragmentService.getFragment<CmsArticle>('fragments.moneyBackGuarantee.url'))
    );

    this.testVariantForPayment = computed(() => (this.isVariantB() ? TestVariant.B : undefined));
  }
}
